<app>
    <virtual if="{state.localeLoaded}">
        <!--
            EULA Acceptance Modal — single global instance (id="eula-modal").
            Opened by the 'eula-required' listener here, by login.tag's
            JWT-launch gate, and by login-form's per-flow handlers. Post-
            accept behavior lives in the modal itself: login routes are
            navigated by 'eula-accepted' window listeners; everywhere else
            the modal reloads the page so data fetched under the EULA gate's
            403s gets refetched (METALARK-883).
        -->
        <eula-acceptance-modal ref="eulaModal"></eula-acceptance-modal>

        <impersonation-banner if="{isActuallyImpersonating()}"></impersonation-banner>
        <debug-session-banner></debug-session-banner>
        <subscription-banner if="{state.isAuthenticated && !isOnPublicPage()}"></subscription-banner>
        <subscription-modal></subscription-modal>
        <session-manager></session-manager>
    </virtual>

    <!-- Always show navbar -->
    <div class="app container mx-auto flex flex-col min-h-screen">
        <div class="mt-4 pt-6 px-8 pb-12 rounded-3xl rounded-b-none bg-white flex flex-1 flex-col">
            <navbar if="{state.localeLoaded}" class="print:hidden"></navbar>
            
            <!-- Only show main content if we have a slice or on allowed pages -->
            <!-- CRITICAL: Always show main-content wrapper to allow dynamic component mounting -->
            <main class="main-content flex-1 flex flex-col" if="{shouldShowMainContent() || state.currentComponent === 'home-page'}">
                <div class="route-container flex-1 flex flex-col">
                    <!-- Loading skeleton for workspace home page -->
                    <div class="workspace-loading" if="{isLoadingWorkspaceContext()}">
                        <div class="flex flex-col w-full gap-12 md:gap-4">
                            <!-- Lark logo and action buttons row -->
                            <div class="flex flex-row gap-12 items-center">
                                <div class="skeleton w-32 lg:w-52 h-32 lg:h-52 hidden md:block"></div>
                                <div class="flex flex-col md:flex-row gap-6 flex-grow">
                                    <div class="skeleton flex-1 h-36 rounded-lg"></div>
                                    <div class="skeleton flex-1 h-36 rounded-lg"></div>
                                    <div class="skeleton flex-1 h-36 rounded-lg"></div>
                                    <div class="skeleton flex-1 h-36 rounded-lg"></div>
                                </div>
                            </div>
                            
                            <!-- Search bar and stats card row -->
                            <div class="flex flex-col md:flex-row gap-12 items-center w-full">
                                <div class="flex flex-col md:flex-row w-full md:flex-grow gap-4 items-center">
                                    <div class="skeleton h-4 w-48"></div>
                                    <div class="skeleton flex-1 h-12 rounded-lg"></div>
                                </div>
                                <div class="border border-1 border-gray-300 rounded-md px-10 py-3 flex flex-col gap-2">
                                    <div class="skeleton h-6 w-32"></div>
                                    <div class="skeleton h-6 w-32"></div>
                                </div>
                            </div>
                            
                            <!-- Content tabs and table row -->
                            <div class="flex flex-col gap-2">
                                <div class="flex flex-row gap-2 items-end">
                                    <div class="skeleton h-8 w-24 rounded-lg"></div>
                                    <div class="skeleton h-8 w-24 rounded-lg"></div>
                                    <div class="flex-grow"></div>
                                    <div class="skeleton h-4 w-16"></div>
                                </div>
                                <!-- Table skeleton -->
                                <div class="flex flex-col gap-2 mt-4">
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Loading skeleton for public pages (login, signup, etc.) -->
                    <div class="public-page-loading" if="{isLoadingPublicPageComponent()}">
                        <div class="card bg-base-100 shadow-xl">
                            <div class="card-body">
                                <!-- Header section -->
                                <div class="flex flex-col gap-4 items-center">
                                    <div class="skeleton h-12 w-48"></div>
                                    <div class="skeleton h-4 w-72"></div>
                                </div>
                                
                                <!-- Form skeleton -->
                                <div class="flex flex-col gap-4 mt-6">
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-12 w-full rounded-lg"></div>
                                    <div class="skeleton h-10 w-full rounded-lg mt-4"></div>
                                </div>
                                
                                <!-- Footer links -->
                                <div class="flex flex-col gap-2 items-center mt-6">
                                    <div class="skeleton h-4 w-56"></div>
                                    <div class="skeleton h-4 w-48"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Custom domain banner (shows when user is on generic domain but has SSO configured) -->
                    <custom-domain-banner if="{state.isAuthenticated && !isLoadingWorkspaceContext()}"></custom-domain-banner>

                    <!-- Dynamic route content will be rendered here -->
                    <div id="route-content" class="flex-1 flex flex-col relative {isLoadingWorkspaceContext() || isLoadingPublicPageComponent() ? 'hidden' : ''}"></div>
                </div>
            </main>
        </div>

    </div>

    <virtual if="{state.localeLoaded}">
        <pre-upload-files-modal></pre-upload-files-modal>
        <upload-files-modal></upload-files-modal>
        <file-picker-modal></file-picker-modal>
        <reupload-modal></reupload-modal>
        <invalid-files-modal></invalid-files-modal>
        <quota-limit-modal></quota-limit-modal>
        <attribute-reason-modal></attribute-reason-modal>
        <language-reconciliation-modal></language-reconciliation-modal>
        <csv-preview-modal></csv-preview-modal>
        <report-print-modal></report-print-modal>
        <loading-overlay></loading-overlay>
    </virtual>

    <script>
        import * as riot from 'riot'
        import { initRouter, setAppInstance, getCurrentComponent, getCurrentQueryParams, navigateTo, getCurrentRoute, parseQueryParams } from './router/routes.js'
        import { getBundleForRoute } from './bundles'
        import { fetchablePlugin } from './plugins/fetchable-plugin.js'
        import { deepEqualPlugin } from './plugins/deep-equal-plugin.js'
        import { debugPlugin } from "./plugins/debug-plugin";
        import { servicesPlugin } from './plugins/services-plugin.js';
        import navbar from "./components/navbar.tag"
        import loadingOverlay from "./components/loading-overlay.tag"
        import impersonationBanner from "./components/admin/impersonation-banner.tag"
        import sessionManager from "./components/auth/session-manager.tag"
        import debugSessionBanner from "./components/admin/debug-session-banner.tag"
        import customDomainBanner from "./components/custom-domain-banner.tag"
        import subscriptionBanner from "./components/subscription-banner.tag"
        import subscriptionModal from "./components/subscription-modal.tag"
        import invalidFilesModal from "./components/invalid-files-modal.tag"
        import preUploadFilesModal from "./components/pre-upload-files-modal.tag"
        import uploadFilesModal from "./components/upload-files-modal.tag"
        import filePickerModal from "./components/admin/modals/file-picker-modal.tag"
        import reuploadModal from "./components/reupload-modal.tag"
        import quotaLimitModal from "./components/quota-limit-modal.tag"
        import attributeReasonModal from "./components/attribute-reason-modal.tag"
        import languageReconciliationModal from "./components/admin/modals/language-reconciliation-modal.tag"
        import csvPreviewModal from "./components/csv-preview-modal.tag"
        import reportPrintModal from "./components/report-print-modal.tag"
        import eulaAcceptanceModal from "./components/auth/eula-acceptance-modal.tag"
        import { themeManager } from './services/theme-manager.js'
        import { PUBLIC_ROUTE_COMPONENTS } from './config/routes.js'
        import { getAllPageModules } from './services/pageLoader.js'
        
        // Lazy store loading to avoid TDZ violations
        let storeInstance = null;
        async function loadStore() {
            if (!storeInstance) {
                const module = await import("./services/store.js");
                storeInstance = module.store;
            }
            return storeInstance;
        }

        // Lazy debugSessionService loading to avoid circular dependencies
        let debugSessionServiceInstance = null;
        async function loadDebugSessionService() {
            if (!debugSessionServiceInstance) {
                const module = await import('./services/debugSessionService.js');
                debugSessionServiceInstance = module.debugSessionService;
            }
            return debugSessionServiceInstance;
        }

        // Lazy imports to break circular dependencies
        // These are imported dynamically where needed instead of at module load time

        // Install plugins
        riot.install(fetchablePlugin)
        riot.install(deepEqualPlugin)
        riot.install(debugPlugin)
        riot.install(servicesPlugin)  // Auto-inject tools (opt-out) and store (opt-in)

        // There was a global `state.DEV` here, set from `import.meta.env.MODE ===
        // 'development'`, which four pages read as "serve canned mock data instead of
        // calling the API". It was written when the mock server was the only way to run
        // the UI locally; once `npm run dev` could point at the real local stack, that
        // condition was true for a session talking to a real API and the item page
        // silently rendered a fixture — a title and description that were not the
        // resource's — in place of content the API was returning correctly.
        //
        // The signal now comes from the server that actually serves mock data: it sets
        // `X-MetaLark-Mock-Api` and `isMockApi()` in api/api.js latches it. The default is
        // therefore "real API", and only the mock server can change it.
        
        // Dynamic page loading with bundle-aware importing
        // Instead of static import.meta.glob that pre-bundles all pages at build time,
        // we use dynamic imports through pageLoader. This ensures:
        // 1. Pages only load when requested (no pre-bundling)
        // 2. Pages load from correct bundle (public, user, customer-admin, system-admin)
        // 3. Circular dependencies are eliminated (imports deferred to runtime)
        // 4. Production build creates proper code splits per bundle
        const pageModules = getAllPageModules();
        
        console.log('[Component Init] pageModules initialized:', {
            isObject: typeof pageModules === 'object',
            keys: pageModules ? Object.keys(pageModules).length : 0,
            pageModulesType: typeof pageModules
        })
        
        // Cache for loaded components
        const loadedComponents = {}
        
        // Registry for riot-registered components (replaces non-existent riot.components)
        const registeredComponents = {}
        
        // Module-scoped helper to ensure pageModules is always accessible
        const componentLoader = {
            pageModules: pageModules,
            loadedComponents: loadedComponents,
            getPageModules() {
                if (!this.pageModules || typeof this.pageModules !== 'object') {
                    console.error('[ComponentLoader] CRITICAL: pageModules not accessible!')
                    return {}
                }
                return this.pageModules
            }
        }
        
        // Initialize only critical components needed at startup
        async function initializeComponents() {
            console.log(`[Component Init] Lazy loading enabled - components will be loaded on demand`)
            const mods = componentLoader.getPageModules()
            console.log(`[Component Init] pageModules keys available: ${Object.keys(mods).length}`)
            
            // Pre-load only the public login component for faster first paint
            try {
                const loginModule = await mods['./pages/login.tag']?.();
                if (loginModule?.default) {
                    componentLoader.loadedComponents['login-page'] = loginModule.default
                    riot.register('login-page', loginModule.default)
                    console.log(`[Component Init] Pre-loaded login-page for public access`)
                }
            } catch (error) {
                console.warn(`[Component Init] Failed to pre-load login-page:`, error)
            }
        }
        
        // Function to get import function for a component name
        function findModuleImportFn(componentName) {
            const mods = componentLoader.getPageModules()
            // Safety check - ensure pageModules is available
            if (!mods || Object.keys(mods).length === 0) {
                console.error(`[Component Find] pageModules not initialized!`)
                return null
            }
            
            const componentFileName = componentName.replace('-page', '')
            const keys = Object.keys(mods)
            
            console.log(`[Component Find] Looking for "${componentName}" (file: "${componentFileName}") among ${keys.length} modules`)
            
            // Search for exact matches in the glob modules
            for (const path of keys) {
                const fileName = path.replace('./pages/', '').replace(/^.*\//, '').replace('.tag', '')
                if (fileName === componentFileName) {
                    console.log(`[Component Find] Found match: ${path} -> ${fileName}`)
                    // Return the import function directly from the object
                    return mods[path]
                }
            }
            
            // If exact match not found, try partial matching (for debugging)
            console.warn(`[Component Find] No exact match for "${componentFileName}". Available files:`)
            for (const path of keys.slice(0, 20)) {
                const fileName = path.replace('./pages/', '').replace(/^.*\//, '').replace('.tag', '')
                console.warn(`  - ${path} (parsed as: ${fileName})`)
            }
            if (keys.length > 20) {
                console.warn(`  ... and ${keys.length - 20} more`)
            }
            
            return null
        }
        
        // Function to get a component (async - loads on demand)
        async function getComponent(componentName) {
            console.log(`[Component Get] Requesting component: ${componentName}`)
            
            try {
                // Integrate with bundleLoader to ensure correct bundle is loaded
                // This breaks the circular dependency by deferring all bundle loading to runtime
                try {
                    const { bundleLoader } = await import('./services/bundleLoader')
                    const routeName = componentName.replace('-page', '')
                    await bundleLoader.ensureBundleForRoute(routeName)
                    console.log(`[Component Get] Bundle loaded for route: ${routeName}`)
                } catch (bundleError) {
                    console.warn(`[Component Get] bundleLoader error (non-critical):`, bundleError)
                    // Continue anyway - component might still load from glob
                }
                
                // Check if already registered with Riot
                if (registeredComponents[componentName]) {
                    console.log(`[Component Get] Component already registered with Riot: ${componentName}`)
                    return registeredComponents[componentName]
                }
                
                // Check if loaded but not registered (shouldn't happen but be safe)
                if (componentLoader.loadedComponents[componentName]) {
                    console.log(`[Component Get] Found cached component: ${componentName}`)
                    // Riot might already have this registered
                    // Only register if not already in our registry
                    if (!registeredComponents[componentName]) {
                        console.log(`[Component Get] Registering cached component with Riot: ${componentName}`)
                        try {
                            riot.register(componentName, componentLoader.loadedComponents[componentName])
                            registeredComponents[componentName] = componentLoader.loadedComponents[componentName]
                        } catch (regError) {
                            console.warn(`[Component Get] Registration error (likely already registered):`, regError.message)
                            // Component is already registered, just return the cached version
                        }
                    } else {
                        console.log(`[Component Get] Component already registered with Riot: ${componentName}`)
                    }
                    return componentLoader.loadedComponents[componentName]
                }
                
                // Try to load from admin loaders if not in public/user pages
                // Admin page loaders are protected and only imported when user has admin access
                let importFn = null
                const routeName = componentName.replace('-page', '')
                
                // First check public/user pages
                let mods = componentLoader.getPageModules()
                if (!mods || Object.keys(mods).length === 0) {
                    console.error(`[Component Get] CRITICAL: pageModules not initialized!`)
                    console.error(`[Component Get] componentLoader.pageModules type: ${typeof componentLoader.pageModules}`)
                    console.error(`[Component Get] mods keys: ${mods ? Object.keys(mods).length : 'undefined'}`)
                    return null
                }
                
                // Find the import function for this component
                importFn = findModuleImportFn(componentName)
                
                // If not found in public/user pages, try admin pages — but
                // ONLY the admin loaders the current user's role entitles them
                // to. Previously we tried both customer-admin and system-admin
                // loaders for every authenticated user, which let a
                // CUSTOMER_ADMIN load system-admin-only pages just by typing
                // the URL (the page chunks were shipped regardless of role).
                //
                // Role-to-loader mapping:
                //   CUSTOMER_ADMIN → customer-admin loader only
                //   SYSTEM_ADMIN   → customer-admin AND system-admin (inherits)
                //   anyone else    → neither
                if (!importFn) {
                    // Resolve user role via the tools service directly — this
                    // is a module-level function, `this.tools` doesn't exist
                    // here (that binding is only set on riot component
                    // instances). Dynamic import mirrors routes.js to avoid
                    // circular deps with the services graph.
                    let userRole = null
                    try {
                        const { tools } = await import('./services/tools.js')
                        const userContext = await tools.getUserContext()
                        userRole = userContext?.role || null
                    } catch (e) {
                        console.debug(`[Component Get] Failed to resolve user role for admin-page gating:`, e.message)
                    }

                    const canAccessCustomerAdmin = userRole === 'CUSTOMER_ADMIN' || userRole === 'SYSTEM_ADMIN'
                    const canAccessSystemAdmin = userRole === 'SYSTEM_ADMIN'
                    // METALARK-929: a non-entered PARTNER_ADMIN loads the partner bundle.
                    // (When it enters a customer it runs as effective CUSTOMER_ADMIN, so
                    // canAccessCustomerAdmin is true then and the customer-admin bundle applies.)
                    const canAccessPartnerAdmin = userRole === 'PARTNER_ADMIN' || userRole === 'SYSTEM_ADMIN'

                    if (canAccessCustomerAdmin || canAccessSystemAdmin || canAccessPartnerAdmin) {
                        console.log(`[Component Get] Not in public/user pages, trying admin loaders for: ${componentName} (role=${userRole})`)
                        try {
                            if (canAccessCustomerAdmin) {
                                try {
                                    const { hasCustomerAdminPage, getAdminCustomerPageImport } = await import('./services/pageLoader.bundle.customer-admin.js')
                                    if (hasCustomerAdminPage(routeName)) {
                                        importFn = getAdminCustomerPageImport(routeName)
                                        if (importFn) {
                                            console.log(`[Component Get] Found in customer admin pages: ${routeName}`)
                                        }
                                    }
                                } catch (e) {
                                    console.debug(`[Component Get] Failed to check customer admin loader:`, e.message)
                                }
                            }

                            if (!importFn && canAccessPartnerAdmin) {
                                try {
                                    const { hasPartnerAdminPage, getAdminPartnerPageImport } = await import('./services/pageLoader.bundle.partner-admin.js')
                                    if (hasPartnerAdminPage(routeName)) {
                                        importFn = getAdminPartnerPageImport(routeName)
                                        if (importFn) {
                                            console.log(`[Component Get] Found in partner admin pages: ${routeName}`)
                                        }
                                    }
                                } catch (e) {
                                    console.debug(`[Component Get] Failed to check partner admin loader:`, e.message)
                                }
                            }

                            if (!importFn && canAccessSystemAdmin) {
                                try {
                                    const { hasSystemAdminPage, getAdminSystemPageImport } = await import('./services/pageLoader.bundle.system-admin.js')
                                    if (hasSystemAdminPage(routeName)) {
                                        importFn = getAdminSystemPageImport(routeName)
                                        if (importFn) {
                                            console.log(`[Component Get] Found in system admin pages: ${routeName}`)
                                        }
                                    }
                                } catch (e) {
                                    console.debug(`[Component Get] Failed to check system admin loader:`, e.message)
                                }
                            }
                        } catch (adminError) {
                            console.warn(`[Component Get] Error trying admin pages:`, adminError)
                        }
                    } else {
                        console.log(`[Component Get] User role ${userRole} cannot access admin pages, falling through to 404`)
                    }
                }
                
                if (!importFn) {
                    console.warn(`[Component Get] Module not found for ${componentName}, trying 404-page`)
                    if (componentLoader.loadedComponents['not-found-page']) {
                        return componentLoader.loadedComponents['not-found-page']
                    }
                    return null
                }
                
                console.log(`[Component Get] Dynamically loading ${componentName}`)
                
                // Call the import function to load the module
                const module = await importFn()
                if (!module?.default) {
                    console.error(`[Component Get] Module does not export default for ${componentName}`)
                    return null
                }
                
                // Cache the loaded component
                componentLoader.loadedComponents[componentName] = module.default
                
                // Inject styles if present
                if (module.default.css && typeof module.default.css === 'string') {
                    injectComponentStyles(module.default.css, componentName)
                }
                
                // Register component globally with Riot
                try {
                    riot.register(componentName, module.default)
                    registeredComponents[componentName] = module.default
                    console.log(`[Component Get] Successfully registered ${componentName}`)
                } catch (regError) {
                    // Component might already be registered from a previous load
                    // Just cache it and continue - it's still usable
                    if (regError.message?.includes('already registered')) {
                        console.log(`[Component Get] Component already registered (expected on reload): ${componentName}`)
                        registeredComponents[componentName] = module.default
                    } else {
                        // Unexpected registration error
                        throw regError
                    }
                }
                
                console.log(`[Component Get] Successfully loaded ${componentName}`)
                return module.default
            } catch (error) {
                console.error(`[Component Get] Failed to load ${componentName}:`, error)
                
                // Fallback to 404 page if available
                if (componentLoader.loadedComponents['not-found-page']) {
                    return componentLoader.loadedComponents['not-found-page']
                }
                return null
            }
        }
        
        // Count slice-scoped role assignments that match the caller's own
        // role tier — i.e., direct workspace roles. Used by the workspace-
        // selection redirect guard to distinguish single-workspace admins
        // (auto-redirect off the picker is fine) from multi-workspace
        // admins (must stay on the picker). Falls back to 0 when the
        // userContext didn't include role assignments, which keeps the
        // guard fail-safe (no redirect rather than wrong redirect).
        function countDirectRoleWorkspaces(userContext) {
            const role = userContext?.role;
            const assignments = userContext?.roleAssignments || [];
            if (!role || assignments.length === 0) return 0;
            return assignments.filter(a => a.sliceId && a.role === role).length;
        }

        // Utility function to inject component styles
        function injectComponentStyles(css, componentName) {
            // Check if styles are already injected
            const existingStyle = document.getElementById(`styles-${componentName}`)
            if (existingStyle) {
                return
            }

            // Create and inject style element
            const styleElement = document.createElement('style')
            styleElement.id = `styles-${componentName}`
            styleElement.textContent = css
            document.head.appendChild(styleElement)
            
            console.log(`[Component Init] Injected styles for ${componentName}`)
        }

        export default {
            navigateTo,
            getCurrentRoute,

            get components() {


                return {
                navbar,
                loadingOverlay,
                impersonationBanner,
                debugSessionBanner,
                customDomainBanner,
                subscriptionBanner,
                subscriptionModal,
                sessionManager,
                invalidFilesModal,
                preUploadFilesModal,
                uploadFilesModal,
                filePickerModal,
                reuploadModal,
                quotaLimitModal,
                attributeReasonModal,
                languageReconciliationModal,
                csvPreviewModal,
                reportPrintModal,
                eulaAcceptanceModal
            };


            },

            state: {
                currentComponent: 'home-page',
                currentQueryParams: {},
                sliceId: null,
                environmentLoaded: false,
                isAuthenticated: false,
                localeLoaded: false
            },

            async isActuallyImpersonating() {
                // Only show banner when user is actually impersonating (has expiresAt)
                // Regular workspace access with just a token should NOT show the banner
                const store = await loadStore();
                const impersonationState = store.getState().impersonation;
                return impersonationState && impersonationState.expiresAt;
            },

            isOnWorkspaceSelectionPage() {
                return this.state.currentComponent === 'select-workspace-page'
            },

            isOnAdminPage() {
                // Defer to the bundle registry instead of a hand-curated
                // list of *-page component names. The admin route set is
                // already enumerated in src/bundles/{customer-admin,
                // system-admin}.config.ts because Lambda@Edge and the
                // BundleLoader rely on it for permission gating and code
                // splitting, so reusing it here means a newly-added
                // admin route only has to be registered in one place
                // (the bundle config) to be recognized as a slice-free
                // page by the workspace-redirect logic.
                //
                // Many admin routes (customer-360, customers, users,
                // audit-logs, …) sit at the top of the hash without an
                // `admin/` prefix, so a path-prefix check would miss
                // them. Routing by bundle type avoids that mismatch.
                //
                // The previous hardcoded list (admin-page, users-page,
                // audit-logs-page, customers-page) omitted customer-360
                // and most other admin surfaces, so refreshing
                // customer-360 as a CUSTOMER_ADMIN with multi-workspace
                // direct roles sent the admin to the workspace picker
                // because the redirect logic treated the page as
                // workspace-required.
                const hash = window.location.hash || '';
                const routeName = hash.replace(/^#\/?/, '').split('?')[0].split('/')[0];
                if (!routeName) return false;
                const bundle = getBundleForRoute(routeName);
                return bundle === 'customer-admin' || bundle === 'system-admin';
            },

            isOnUserPage() {
                // User-level pages that don't require slice context.
                // METALARK-835: the /invitations list operates on the
                // signed-in user's pending invites (no workspace
                // context); it lives in the user bundle so add it here
                // alongside settings so shouldShowMainContent() lets
                // the route-content target render even when the user
                // doesn't have a selected slice yet.
                const userPages = ['settings-page', 'invitations-page'];
                return userPages.includes(this.state.currentComponent);
            },

            isTargetingPublicPage() {
                // Check if the user is trying to navigate to a public page by inspecting the URL hash
                // This is used early before component state is updated by the router
                const currentRoute = getCurrentRoute();
                return PUBLIC_ROUTE_COMPONENTS.some(publicComponent =>
                    currentRoute.toLowerCase().includes(publicComponent.replace('-page', ''))
                );
            },

            isOnPublicPage() {
                // Public pages that don't require authentication or workspace
                // Uses centralized config from config/routes.js
                return PUBLIC_ROUTE_COMPONENTS.includes(this.state.currentComponent);
            },

            isLoadingWorkspaceContext() {
                // Show skeleton ONLY on workspace pages (non-public) while component is still loading
                // Hide skeleton as soon as any non-public component mounts in the DOM
                // This prevents showing skeleton when admin pages, user pages, or other workspace content loads
                const isOnPublicPage = this.isOnPublicPage();
                const isAuthenticated = this.state.isAuthenticated;

                // Never show skeleton on public pages
                if (isOnPublicPage) {
                    return false;
                }

                // Show skeleton while locale is loading (even before auth completes to prevent content flash)
                if (!this.state.localeLoaded) {
                    return true;
                }

                // Check if ANY component is mounted in the DOM
                // Once ANY component exists, hide the skeleton immediately
                const routeContent = document.getElementById('route-content');
                if (!routeContent) {
                    return true; // Show skeleton while waiting for route-content
                }

                // Hide skeleton if ANY child elements exist (any component is mounting/mounted)
                const hasAnyComponent = routeContent.children.length > 0;

                // Show skeleton ONLY if route-content is completely empty (async loading in progress)
                const shouldShowSkeleton = !hasAnyComponent;

                console.log('[App] isLoadingWorkspaceContext:', {
                    shouldShowSkeleton,
                    isOnPublicPage,
                    isAuthenticated,
                    hasAnyComponent,
                    localeLoaded: this.state.localeLoaded,
                    currentComponent: this.state.currentComponent,
                    sliceId: this.state.sliceId
                });

                return shouldShowSkeleton;
            },

            isLoadingPublicPageComponent() {
                // Show public page skeleton while loading public page components (login, signup, etc.)
                // Hide skeleton as soon as the specific component mounts

                const isOnPublicPage = this.isOnPublicPage();
                const isUnauthenticated = !this.state.isAuthenticated;

                // Workspace skeleton takes priority
                const workspaceSkeletonShowing = this.isLoadingWorkspaceContext();
                if (workspaceSkeletonShowing) {
                    return false;
                }

                // Don't show skeleton on 404 pages
                if (this.state.currentComponent === 'not-found-page') {
                    return false;
                }

                // Don't show skeleton if user is authenticated
                if (!isUnauthenticated) {
                    return false;
                }

                // Don't show skeleton on non-public pages
                if (!isOnPublicPage) {
                    return false;
                }

                // Show skeleton while locale is loading (for public pages)
                if (!this.state.localeLoaded) {
                    return true;
                }
                
                // Check if the specific public page component is mounted in the DOM
                const routeContent = document.getElementById('route-content');
                if (!routeContent) {
                    return true; // Show skeleton while waiting for route-content to exist
                }
                
                // Hide skeleton if the specific component is mounted
                // This handles the case where component mounting happens synchronously/quickly
                // before the skeleton rendering gets a chance to update
                const currentComponentTag = this.state.currentComponent;
                const isCurrentlyMounted = !!routeContent.querySelector(currentComponentTag);
                
                // ALSO hide if ANY child elements exist (component is rendering)
                const hasAnyContent = routeContent.children.length > 0;
                
                // Show skeleton ONLY if BOTH conditions are true:
                // 1. The specific component is NOT mounted, AND
                // 2. route-content is completely empty
                const shouldShowSkeleton = !isCurrentlyMounted && !hasAnyContent;
                
                console.log('[App] isLoadingPublicPageComponent:', {
                    shouldShowSkeleton,
                    isOnPublicPage,
                    isUnauthenticated,
                    isCurrentlyMounted,
                    hasAnyContent,
                    currentComponentTag,
                    workspaceSkeletonShowing
                });
                
                return shouldShowSkeleton;
            },

            shouldShowMainContent() {
                // Show main content if:
                // 1. We have a sliceId (normal workspace operation)
                // 2. On workspace selection page
                // 3. On admin pages (don't require slice)
                // 4. On user pages (settings, profile - don't require slice)
                // 5. Loading workspace context on home page (skeleton state)
                // 6. We have a workspace token (even if sliceId not set yet)
                // 7. On login page (needs #route-content to mount)
                // 8. User is authenticated (verified via /auth/me call)
                // 9. On public pages (verify-email, etc.)
                // 10. CRITICAL: On home page with authenticated user (even if sliceId not ready yet)
                const hasSlice = !!this.state.sliceId;
                const isWorkspaceSelection = this.isOnWorkspaceSelectionPage();
                const isAdmin = this.isOnAdminPage();
                const isUserPage = this.isOnUserPage();
                const isLoadingContext = this.isLoadingWorkspaceContext();
                const hasToken = !!localStorage.getItem('authToken');
                const isPublic = this.isOnPublicPage();
                const isAuthenticated = this.state.isAuthenticated;
                const isOnHome = this.state.currentComponent === 'home-page';

                // Show main content when authenticated OR on public pages
                // IMPORTANT: Allow home-page to mount when authenticated even without sliceId
                // This happens after login before workspace context is loaded
                const shouldShow = hasSlice || isWorkspaceSelection || isAdmin || isUserPage || isLoadingContext || hasToken || isPublic || isAuthenticated || (isOnHome && isAuthenticated);

                console.log('[App] shouldShowMainContent:', {
                    shouldShow,
                    hasSlice,
                    isWorkspaceSelection,
                    isAdmin,
                    isUserPage,
                    isLoadingContext,
                    hasToken,
                    isPublic,
                    isAuthenticated,
                    isOnHome,
                    currentComponent: this.state.currentComponent
                });
                
                return shouldShow;
            },

            onBeforeMount() {
                let initialComponent
                const hash = window.location.hash;

                if (!hash || hash === '' || hash === '#' || hash === '#home') {
                    initialComponent = 'home-page'
                } else {
                    const routeName1 = hash.replace('#', '').split('?')[0];
                    const routeName = routeName1.split('/').pop();

                    // Safety check: if routeName is empty or just special chars, default to home
                    if (!routeName || routeName.trim() === '' || routeName === '=' || routeName.match(/^[?=&]+$/)) {
                        console.warn('[App] Invalid route name detected, defaulting to home-page');
                        initialComponent = 'home-page'
                    } else {
                        initialComponent = `${routeName}-page`
                    }
                }

                const initialQueryParams = parseQueryParams(hash)

                this.prevComponent = initialComponent
                this.prevQueryParams = initialQueryParams
                this.state.currentComponent = initialComponent
                this.state.currentQueryParams = initialQueryParams
            },

            async onMounted() {
                // Initialize theme first (before any rendering)
                themeManager.initialize()
                
                // IMPORTANT: Preserve query parameters BEFORE any redirects happen
                // This ensures that if user visits /#?login=oauth while unauthenticated,
                // the params are saved before router redirects to login page
                // Helper function to preserve query params
                const preserveQueryParams = (currentHash = window.location.hash) => {
                    if (currentHash && currentHash.includes('?')) {
                        // Split on ? to get the part after the ?
                        const hashParts = currentHash.split('?');
                        const queryString = hashParts[1];
                        
                        // Only preserve if we have actual query params
                        if (queryString) {
                            console.log('[App] Preserving query params from URL:', queryString);
                            localStorage.setItem('cached_query_params', queryString);
                            return true;
                        }
                    }
                    return false;
                };
                
                // Check initial URL for query params
                preserveQueryParams();
                
                // Initialize lazy loading system (only pre-load login component)
                await initializeComponents()
                
                // Register this component instance with the router
                setAppInstance(this)

                // Initialize auth context BEFORE mounting components
                // This ensures navbar and other components have correct user context
                // Note: Authentication guard is now handled by the router
                // Skip auth initialization for public pages
                if (!this.isOnPublicPage()) {
                    console.log('[App] [INIT] Starting auth initialization for non-public page');

                    // The imp_ token is already in Redux from store.js's initial
                    // state seed (synchronous localStorage read), so the axios
                    // interceptor has been attaching Bearer since the first
                    // render. initImpersonation revalidates and fills in the
                    // rest of the session (slice, reason, expiresAt, customer
                    // name) or clears the token if the backend no longer
                    // recognizes it.
                    await this.initImpersonation()    // Validate token + populate session state

                    await this.initUserContext()      // Cache user context from /auth/me

                    // If user is not authenticated after initUserContext, skip the rest
                    // This prevents cascading 401 errors from feature flags, quota, workspace checks
                    if (!this.state.isAuthenticated) {
                        console.log('[App] [INIT] User not authenticated, skipping remaining initialization');
                        console.log('[App] [INIT] Router will handle redirect to login');
                        // Don't call initSession, initFeatureFlags, initQuota
                        // Let the router redirect to login-page automatically
                        // Still initialize locale (will default to English) to allow page to render
                        await this.initLocale();
                    } else {
                        console.log('[App] [INIT] User authenticated, continuing initialization');
                        await this.initSession()          // Call /environment with token in headers
                        await this.initFeatureFlags()     // Load feature flags for current user
                        await this.initQuota()            // Load quota details for current user
                        await this.initSubscriptionStatus() // Load subscription status into store (shared with banner + upload)
                        await this.initLocale()           // Load locale based on customer defaultLanguage
                        await this.checkLanguageReconciliation() // Check for language mismatch (admin only)
                    }
                } else {
                    console.log('[App] On public page, skipping auth initialization');
                    // Still initialize locale for public pages (will default to English)
                    await this.initLocale();
                }

                // Pre-load session renewal service early if authenticated
                // This ensures the inactivity prompt and session monitoring are ready immediately
                if (!this.isOnPublicPage() && this.state.isAuthenticated) {
                    try {
                        await import('./services/sessionRenewalService.js')
                        console.log('[App] Session monitoring service pre-loaded');
                    } catch (error) {
                        console.warn('[App] Failed to preload session service:', error);
                    }
                }

                // Mount the initial component AFTER auth AND locale are initialized
                // This prevents content flash before locale is ready
                this.mountCurrentComponent();
                
                // Initialize the router after mounting
                initRouter()

                const store = await loadStore();
                this.unsubscribe = store.subscribe(() => {
                    const newSliceId = store.getState().sliceId;
                    
                    // If sliceId changed to empty/null, reset environmentLoaded flag
                    // This handles workspace switching where sliceId is cleared then reloaded
                    if (newSliceId !== this.state.sliceId && !newSliceId) {
                        console.log('[App] SliceId cleared, resetting environmentLoaded flag');
                        this.state.environmentLoaded = false;
                        this.update();
                    }
                    
                    if (newSliceId !== this.state.sliceId) {
                        console.log('[App] Redux sliceId changed:', this.state.sliceId, '->', newSliceId);
                        this.state.sliceId = newSliceId;
                        this.update();
                        // Recheck workspace selection when sliceId changes
                        // This handles early sliceId dispatch from /auth/me
                        this.checkAndRedirectToWorkspaceSelection();
                    }

                });

                // Only check workspace selection if user is authenticated
                // Prevents cascading 401 errors for unauthenticated users
                if (this.state.isAuthenticated) {
                    await this.checkAndRedirectToWorkspaceSelection()
                } else {
                    console.log('[App] [INIT] User not authenticated, skipping workspace selection check');
                    console.log('[App] [INIT] Router will redirect to login-page automatically');
                }
                
                // Set up EULA monitoring and event listener
                this.setupEulaMonitoring()

                // Set up keyboard shortcuts
                this.setupKeyboardShortcuts()

                // Set up popstate listener to detect browser back/forward navigation
                this.setupBackNavigationDetection()
            },

            onUnmounted() {
                this.unsubscribe();
                // Clean up keyboard shortcuts
                if (this.keyboardHandler) {
                    document.removeEventListener('keydown', this.keyboardHandler);
                }
                // Clean up EULA monitoring
                if (this.eulaEventHandler) {
                    window.removeEventListener('eula-required', this.eulaEventHandler);
                }
                this.stopEulaMonitoring();
                // Clean up back navigation detection listener
                if (this.pageshowHandler) {
                    window.removeEventListener('pageshow', this.pageshowHandler);
                }
            },

            setupEulaMonitoring() {
                // Set up global EULA event listener
                // This listener catches EULA requirements from both API interceptor and periodic checks
                this.eulaEventHandler = (event) => {
                    console.log('[App] EULA required event received:', event.detail);

                    // Check if we're on a public page - don't show EULA modal on login/signup
                    if (this.isOnPublicPage()) {
                        console.log('[App] On public page, skipping EULA modal');
                        return;
                    }

                    // Check if modal is already open (prevent duplicate modals)
                    const dialog = document.querySelector('#eula-modal');
                    if (dialog && dialog.hasAttribute('open')) {
                        console.log('[App] EULA modal already open, skipping');
                        return;
                    }

                    // Open the EULA modal
                    if (dialog && typeof dialog.showModal === 'function') {
                        console.log('[App] Opening EULA modal');
                        dialog.showModal();
                    } else {
                        console.error('[App] EULA dialog not found or not supported');
                    }
                };

                window.addEventListener('eula-required', this.eulaEventHandler);
                console.log('[App] EULA event listener registered');

                // Start periodic EULA monitoring if user is authenticated
                if (this.state.isAuthenticated) {
                    this.startEulaMonitoring();
                }
            },

            async startEulaMonitoring() {
                try {
                    const { eulaMonitorService } = await import('./services/eulaMonitorService.js');
                    eulaMonitorService.startMonitoring();
                    console.log('[App] EULA monitoring started');
                } catch (error) {
                    console.error('[App] Failed to start EULA monitoring:', error);
                }
            },

            async stopEulaMonitoring() {
                try {
                    const { eulaMonitorService } = await import('./services/eulaMonitorService.js');
                    eulaMonitorService.stopMonitoring();
                    console.log('[App] EULA monitoring stopped');
                } catch (error) {
                    console.error('[App] Failed to stop EULA monitoring:', error);
                }
            },

            setupKeyboardShortcuts() {
                this.keyboardHandler = async (e) => {
                    // Only trigger if Alt (or Option on Mac) is pressed
                    if (!e.altKey) return;
                    
                    // Don't trigger if user is typing in an input field
                    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
                    
                    // Use e.code for physical key position (works across keyboard layouts)
                    // Alt+S or Option+S - Navigate to Settings
                    if (e.code === 'KeyS') {
                        e.preventDefault();
                        console.log('[App] Keyboard shortcut: Navigating to settings');
                        navigateTo('settings');
                        return;
                    }
                    
                    // Alt+A or Option+A - Navigate to Admin (if user is admin)
                    if (e.code === 'KeyA') {
                        e.preventDefault();
                        console.log('[App] Alt+A detected, checking admin status...');
                        try {
                            const { tools } = await import('./services/tools');
                            const userContext = await tools.getUserContext();
                            const isAdmin = ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN', 'TENANT_ADMIN'].includes(userContext.role);
                            
                            if (isAdmin) {
                                console.log('[App] Keyboard shortcut: Navigating to admin');
                                navigateTo('admin');
                            } else {
                                console.log('[App] Keyboard shortcut: User not admin, ignoring Alt+A');
                            }
                        } catch (error) {
                            console.error('[App] Failed to check admin status for keyboard shortcut:', error);
                        }
                        return;
                    }

                    // Alt+D or Option+D - Toggle Debug Session Banner
                    if (e.code === 'KeyD') {
                        e.preventDefault();
                        console.log('[App] Alt+D detected, toggling debug session banner');
                        try {
                            const debugSessionService = await loadDebugSessionService();
                            debugSessionService.toggleDebugMode();
                        } catch (error) {
                            console.error('[App] Failed to load/toggle debug session:', error);
                        }
                        return;
                    }
                };
                
                document.addEventListener('keydown', this.keyboardHandler);
                console.log('[App] Keyboard shortcuts enabled: Alt+S (Settings), Alt+A (Admin), Alt+D (Debug Session)');
            },

            setupBackNavigationDetection() {
                // Listen for browser back/forward button clicks
                // This is critical for SPAs with hash routing where Performance API may not detect navigation

                // Chrome 145+ uses aggressive bfcache (back-forward cache)
                // When restored from bfcache, the navigation type stays as original "navigate"
                // Must use pageshow event to detect bfcache restorations
                this.pageshowHandler = (event) => {
                    if (event.persisted) {
                        // Page was restored from bfcache (back/forward navigation)
                        console.log('[App] Back/Forward navigation detected via bfcache restoration');
                        sessionStorage.setItem('browser-back-forward', 'true');
                    } else {
                        // Fresh page load - check navigation type for traditional back/forward
                        const perfEntries = performance.getEntriesByType?.('navigation');
                        const navEntry = perfEntries?.[0];
                        const navigationType = navEntry?.type || navigation?.currentEntry?.navigationType;

                        if (navigationType === 'back_forward' || navigationType === 'traverse') {
                            console.log('[App] Back/Forward navigation detected via standard navigation (type:', navigationType, ')');
                            sessionStorage.setItem('browser-back-forward', 'true');
                        } else {
                            console.log('[App] Fresh page load (type:', navigationType, ')');
                        }
                    }
                };

                // In-app hash-based back/forward detection (forward nav-tab clicks vs a
                // real back/forward-button press) is handled by routes.js's
                // markBackNavigationIfTraversal(), keyed off `hashchange` + history.state.
                // That replaced an earlier `popstate`-based guard here: popstate proved
                // unreliable in some browsers/automation contexts (firing zero, multiple,
                // or delayed times for an ordinary forward hash write), which
                // intermittently misclassified forward nav-tab clicks as a back press.
                // hashchange fires exactly once and synchronously for every hash-based
                // navigation, forward or back, so it doesn't have that problem.
                window.addEventListener('pageshow', this.pageshowHandler);
                console.log('[App] Back navigation detection enabled (pageshow listener)');
            },

            onUpdated() {
                // Mount if either the component changed OR query params changed
                const componentChanged = this.state.currentComponent !== this.prevComponent;
                const queryParamsChanged = JSON.stringify(this.state.currentQueryParams) !== JSON.stringify(this.prevQueryParams || {});

                // Pages that treat their own query params as internal state
                // (back-button-driven step navigation, tab switches, etc.)
                // must be exempt from the query-param-triggered remount —
                // otherwise the browser back button wipes whatever state
                // the page accumulated before the nav.
                //   - home-page: ignores query params entirely.
                //   - new-customer-page: uses ?step= to drive wizard steps
                //     while the form state lives on the component instance.
                const QUERY_STABLE_COMPONENTS = new Set([
                    'home-page',
                    'new-customer-page',
                    // content-repositories-page uses ?view= to drive
                    // the segmented-pill subtab (Connections / Pending /
                    // Rules / Activity) and lazy-loads each embed once
                    // per mount. Letting the App remount on every pill
                    // click throws away `loadedSubtabs`, restarts the
                    // chunk import for the freshly-active embed, and
                    // briefly renders the default subtab against the
                    // requested ?view= until onMounted catches up — the
                    // visible bounce-back. The page already drives its
                    // own URL via pushState in switchSubtab, so the
                    // remount is pure churn.
                    'content-repositories-page',
                    // migration-page uses ?target=&view= to drive the
                    // overview / target-detail / add-files views while the
                    // loaded projects/targets/placements/pushes live on the
                    // component. It pushState's each view and syncs from the
                    // URL on popstate, so a query-param remount would just
                    // re-fetch everything and wipe the open target on back.
                    'migration-page',
                ]);
                const shouldRemount = componentChanged ||
                    (queryParamsChanged && !QUERY_STABLE_COMPONENTS.has(this.state.currentComponent));

                if (shouldRemount) {
                    // Mount the current component
                    this.mountCurrentComponent()
                    this.prevComponent = this.state.currentComponent
                    this.prevQueryParams = this.state.currentQueryParams

                    // Check if we need to redirect to workspace selection
                    this.checkAndRedirectToWorkspaceSelection()
                }


            },

            async mountCurrentComponent() {
                // Capture the target component up front. mountCurrentComponent
                // can be invoked multiple times in quick succession during
                // initial load (route resolution + auth + locale + bundle
                // each landing on its own onUpdated tick) — without a
                // generation guard a slow load for one route can resolve
                // after a faster load for the *next* route, then try to
                // mount stale-name → "never registered" error.
                const myGen = (this._mountGen = (this._mountGen || 0) + 1);
                const targetComponent = this.state.currentComponent;
                const isStale = () => myGen !== this._mountGen;

                console.log('[App] mountCurrentComponent called for:', targetComponent);
                const routeContent = document.getElementById('route-content');
                if (!routeContent) {
                    console.error('[App] route-content element not found!');
                    return;
                }

                // Show a skeleton immediately so the route swap doesn't
                // flash the previous page's content while the next
                // bundle / register cycle resolves. A later successful
                // mount replaces this innerHTML wholesale; a later
                // failed mount path also paints a skeleton, so this is
                // safe to set unconditionally.
                if (!this.mountedComponent || this.mountedComponent.root?.tagName?.toLowerCase() !== targetComponent) {
                    routeContent.innerHTML = `
                        <div class="p-6 animate-pulse">
                            <div class="skeleton h-8 w-1/3 rounded-md mb-4"></div>
                            <div class="flex gap-3 mb-6">
                                <div class="skeleton h-5 w-24 rounded-full"></div>
                                <div class="skeleton h-5 w-24 rounded-full"></div>
                                <div class="skeleton h-5 w-24 rounded-full"></div>
                            </div>
                            <div class="skeleton h-40 w-full rounded-lg mb-3"></div>
                            <div class="skeleton h-40 w-full rounded-lg"></div>
                        </div>
                    `;
                }

                // Homepage-specific handling: prevent double-mount on initial load
                // If we're already showing home-page and trying to mount it again, skip the remount
                if (targetComponent === 'home-page' && this.mountedComponent) {
                    const currentlyMountedTag = routeContent.querySelector('home-page');
                    if (currentlyMountedTag) {
                        console.log('[App] home-page already mounted, skipping remount to prevent data reload');
                        return;
                    }
                }

                // --- Phase 0: Load the component on demand ---
                try {
                    console.log(`[App] Loading component on demand: ${targetComponent}`);
                    const component = await getComponent(targetComponent);

                    // Bail if a newer mount has been requested while we
                    // were waiting on the import. The newer call owns
                    // the routeContent now — touching it from this
                    // stale closure would clobber the next page mid-load.
                    if (isStale()) {
                        console.log(`[App] Stale mount aborted (target was ${targetComponent}, current is ${this.state.currentComponent})`);
                        return;
                    }

                    if (!component) {
                        console.error('[App] Failed to load component (returned null):', targetComponent);
                        const mods = componentLoader.getPageModules()
                        console.log('[App] Available modules:', Object.keys(mods).slice(0, 10), '...');
                        // Try to load not-found page instead
                        if (targetComponent !== 'not-found-page') {
                            console.log('[App] Attempting to load not-found-page');
                            this.state.currentComponent = 'not-found-page';
                            this.update();
                        }
                        return;
                    }
                    console.log(`[App] Component loaded successfully: ${targetComponent}`);
                } catch (error) {
                    if (isStale()) return;
                    console.error('[App] Critical error loading component:', error);
                    console.error('[App] Stack:', error instanceof Error ? error.stack : 'No stack trace');
                    // Try to load not-found page instead
                    if (targetComponent !== 'not-found-page') {
                        console.log('[App] Attempting to load not-found-page after error');
                        this.state.currentComponent = 'not-found-page';
                        this.update();
                    }
                    return;
                }

                // --- Phase 1: Unmount previous component ---
                if (this.mountedComponent) {
                    console.log('[App] Unmounting previous component...');

                    // Force Riot's lifecycle to complete before moving on
                    try {
                        await new Promise((resolve) => {
                            // Riot unmount() is synchronous, but we yield one microtask to flush DOM updates
                            this.mountedComponent.unmount(true);
                            requestAnimationFrame(resolve); // wait one frame for DOM cleanup
                        });
                    } catch (err) {
                        console.warn('[App] Error while unmounting previous component:', err);
                    }

                    if (isStale()) {
                        console.log('[App] Stale mount aborted after unmount');
                        return;
                    }
                    this.mountedComponent = null;
                    routeContent.innerHTML = '';
                }

                // --- Phase 2: Mount new component ---
                if (!targetComponent) {
                    console.error('[App] No currentComponent set');
                    return;
                }

                console.log(`[App] Mounting component: ${targetComponent}`);
                console.log(`[App] currentQueryParams:`, this.state.currentQueryParams);

                // Build attributes from query params
                const attributesStr = Object.entries(this.state.currentQueryParams)
                    .map(([k, v]) => ` ${k}="${v}"`)
                    .join('');

                const htmlContent = `<${targetComponent}${attributesStr}></${targetComponent}>`;
                console.log(`[App] Creating component HTML:`, htmlContent);
                routeContent.innerHTML = htmlContent;

                const componentElement = routeContent.querySelector(targetComponent);
                if (componentElement) {
                    console.log('[App] Found component element, mounting with riot.mount()');

                    // First attempt — most common path.
                    let mountResult = this.tryMount(componentElement);

                    // Retry once if Riot says the tag isn't registered.
                    // Race: getComponent may have resolved before its
                    // own riot.register call was committed (the cache
                    // entry is set but the global registry threw
                    // "already registered" earlier and we swallowed
                    // it, leaving the registry stale). Re-running
                    // getComponent forces a clean re-register.
                    if (mountResult.error && this.isRegistrationError(mountResult.error)) {
                        try {
                            await getComponent(targetComponent);
                        } catch (_) {
                            // Swallow — handled below.
                        }
                        if (isStale()) return;
                        mountResult = this.tryMount(componentElement);
                    }

                    if (mountResult.mounted) {
                        this.mountedComponent = mountResult.mounted;
                        console.log('[App] Component mounted successfully:', targetComponent);
                        // Successful mount — any pending "show error"
                        // timer from a previous failed mount is now
                        // moot. Clear it so it can't fire late.
                        if (this._pendingMountErrorTimer) {
                            clearTimeout(this._pendingMountErrorTimer);
                            this._pendingMountErrorTimer = null;
                        }

                        // Force skeleton re-evaluation after component mounts
                        // This ensures skeleton visibility re-renders based on DOM content
                        // Without this, Riot might not re-evaluate the if condition if state hasn't changed
                        this.update();
                        return;
                    }

                    // Mount failed (or returned empty). Don't escalate
                    // to console.error yet — the boot sequence often
                    // races: bundle / register / locale all land on
                    // their own ticks. Keep the skeleton up and arm a
                    // grace-period timer; a subsequent successful mount
                    // wipes it before it ever fires.
                    if (mountResult.error) {
                        console.warn('[App] riot.mount() failed for', targetComponent, '— keeping skeleton, will retry through any newer mount call. Error:', mountResult.error.message || mountResult.error);
                    } else {
                        console.warn('[App] riot.mount() returned empty array for', targetComponent, '— keeping skeleton');
                    }

                    if (this._pendingMountErrorTimer) {
                        clearTimeout(this._pendingMountErrorTimer);
                    }
                    this._pendingMountErrorTimer = setTimeout(() => {
                        // Only surface the error if the app didn't
                        // manage to mount anything (i.e., the skeleton
                        // is still the only child). Otherwise a later
                        // successful mount already replaced routeContent
                        // and the error would never belong here.
                        if (!this.mountedComponent) {
                            console.error('[App] No component mounted after grace period for', targetComponent);
                            let errorMsg = window.locale?.t('error_component_mount');
                            if (!errorMsg || errorMsg === 'error_component_mount') {
                                errorMsg = 'Error mounting component. Check console for details.';
                            }
                            routeContent.innerHTML = `<div style="padding: 20px; color: red;">${errorMsg}</div>`;
                        }
                        this._pendingMountErrorTimer = null;
                    }, 1500);
                } else {
                    console.error('[App] Component element not found in DOM:', targetComponent);
                    console.error('[App] Expected tag name:', targetComponent);
                    console.error('[App] HTML content:', routeContent.innerHTML.substring(0, 200));
                }
            },

            // Wrap riot.mount in a normalized result object so the
            // call site can reason about success / failure / empty
            // returns without try/catching at every step.
            tryMount(componentElement) {
                try {
                    const mounted = riot.mount(componentElement);
                    if (mounted && mounted.length > 0) {
                        return { mounted: mounted[0] };
                    }
                    return { mounted: null };
                } catch (error) {
                    return { error };
                }
            },

            isRegistrationError(error) {
                const msg = String(error?.message || error || '');
                return msg.includes('was never registered') || msg.includes('not registered');
            },

            async initSession() {
                console.log('[App] initSession starting...');
                try {
                    // Check if we have an impersonation session with targetSliceId
                    const store = await loadStore();
                    const impersonationState = store.getState().impersonation;
                    if (impersonationState && impersonationState.targetSliceId) {
                        console.log('[App] [IMPERSONATION DEBUG] Using targetSliceId from impersonation session:', impersonationState.targetSliceId);
                        store.dispatch({ type: 'setSliceId', payload: impersonationState.targetSliceId });
                        console.log('[App] Dispatched targetSliceId to Redux:', impersonationState.targetSliceId);
                        return; // Skip /environment call, we have all we need from impersonation
                    }
                    
                    // Normal flow: call /environment to get sliceId
                    const { environmentService } = await import('./api/environmentService');
                    const environment = await environmentService.get();
                    console.log('[App] Environment response:', environment);
                    
                    // IMPORTANT: Only dispatch sliceId from /environment if it's populated
                    // If /environment returns empty sliceId, don't overwrite one from /auth/me
                    // /auth/me sliceId is more reliable for determining workspace context
                    if (environment.sliceId) {
                        store.dispatch({ type: 'setSliceId', payload: environment.sliceId });
                        console.log('[App] Dispatched sliceId from /environment to Redux:', environment.sliceId);
                    } else {
                        console.log('[App] /environment returned empty sliceId, preserving existing value from /auth/me');
                    }
                } catch (error) {
                    console.error('[App] initSession error:', error);
                } finally {
                    // Mark environment as loaded regardless of success/failure
                    console.log('[App] Marking environment as loaded');
                    this.update({ environmentLoaded: true });
                }
            },

            async initImpersonation() {
                // Check localStorage for persisted authentication token
                const authToken = localStorage.getItem('authToken');

                if (!authToken) {
                    console.log('[App] No authToken in localStorage, relying on cookie authentication');
                    return; // No token to restore - will use cookie auth
                }
                
                console.log('[App] Found authToken in localStorage, checking if valid...');
                console.log('[App] Token type:',
                    authToken.startsWith('imp_') ? 'impersonation'
                        : authToken.startsWith('wsa_') ? 'workspace-access'
                        : authToken.startsWith('ws_') ? 'workspace'
                        : 'session');

                try {
                    const store = await loadStore();
                    if (authToken.startsWith('wsa_')) {
                        // Direct-role workspace-access session. Validate via
                        // GET /access-sessions/current which returns the
                        // caller's current active session regardless of kind
                        // — separate endpoint from the impersonation-only
                        // /impersonate/sessions list. Mirrors the imp_ flow:
                        // optimistic setImpersonation with just the token so
                        // axios attaches the Bearer for the validation call,
                        // then replace with the full session or clear on 401.
                        const existingState = store.getState().impersonation;
                        if (existingState && existingState.token === authToken && existingState.targetSliceId) {
                            console.log('[App] Full workspace-access state already in Redux, skipping validation');
                            return;
                        }
                        const { impersonationService } = await import('./api/impersonationService.js');

                        store.dispatch({
                            type: 'setImpersonation',
                            payload: { token: authToken }
                        });

                        try {
                            const { session } = await impersonationService.getCurrentSession({ skipLoading: true });
                            if (session && session.token === authToken) {
                                store.dispatch({
                                    type: 'setImpersonation',
                                    payload: {
                                        token: session.token,
                                        kind: session.kind || 'WORKSPACE_ACCESS',
                                        targetSliceId: session.targetSliceId,
                                        // Pass targetSliceName through as-is.
                                        // The previous fallback to targetSliceId
                                        // silently turned a missing name into a
                                        // UUID-shaped "name" in Redux, which made
                                        // the workspace switcher render the raw
                                        // sliceId as the workspace label. Leaving
                                        // it undefined lets the switcher's
                                        // existing display chain
                                        // (findSliceNameById → targetSliceName →
                                        // targetSliceId) pick the right value
                                        // from the workspaces list.
                                        targetSliceName: session.targetSliceName,
                                        targetCustomerId: session.targetCustomerId,
                                        targetCustomerName: session.targetCustomerName,
                                        expiresAt: session.expiresAt,
                                    }
                                });
                                console.log('[App] Valid workspace-access session restored');
                            } else {
                                console.warn('[App] Workspace-access token invalid or expired, clearing');
                                localStorage.removeItem('authToken');
                                store.dispatch({ type: 'clearImpersonation' });
                            }
                        } catch (validationError) {
                            // Only clear on a definitive auth failure (401). A 402
                            // from the subscription middleware, a 5xx, or a network
                            // blip leaves the session valid server-side — wiping
                            // the token in those cases locks the user out of their
                            // own live session. The full catch below mirrors this.
                            const status = validationError?.response?.status;
                            if (status === 401) {
                                console.warn('[App] Workspace-access validation 401, clearing:', validationError);
                                localStorage.removeItem('authToken');
                                store.dispatch({ type: 'clearImpersonation' });
                            } else {
                                console.warn('[App] Workspace-access validation failed (non-401, preserving token):', status, validationError?.message);
                            }
                        }
                        return;
                    }

                    if (authToken.startsWith('imp_')) {
                        // Impersonation token - validate with API before restoring.
                        // This runs before initUserContext so /auth/me can attach the
                        // Bearer; a 401 here means the imp_ token is stale and we clear
                        // it, then /auth/me falls back to cookie auth (admin context).
                        // The outer isOnPublicPage() check already filters out
                        // login/logout pages, so the previously needed isAuthenticated
                        // guard is redundant here.

                        // OPTIMIZATION: Check if we already have full impersonation state in Redux
                        // This happens when coming from select-workspace after starting/resuming a session
                        const existingState = store.getState().impersonation;
                        if (existingState && existingState.token === authToken && existingState.targetSliceId) {
                            console.log('[App] [IMPERSONATION DEBUG] Full impersonation state already in Redux, skipping validation');
                            return; // Already have valid state, skip API call
                        }
                        
                        const { impersonationService } = await import('./api/impersonationService.js');
                        
                        // Temporarily set token for this validation request
                        store.dispatch({
                            type: 'setImpersonation',
                            payload: { token: authToken }
                        });
                        
                        // Force refresh on initial load to validate token
                        const sessions = await impersonationService.getSessions(true, { skipLoading: true });
                        
                        // Find the current session
                        const currentSession = sessions.sessions?.find(s => s.token === authToken);
                        
                        if (currentSession) {
                            // Valid session - restore full state. Don't
                            // substitute targetSliceId for a missing
                            // targetSliceName — see the wsa_ branch above
                            // for the rationale; same fix.
                            store.dispatch({
                                type: 'setImpersonation',
                                payload: {
                                    token: currentSession.token,
                                    targetSliceId: currentSession.targetSliceId,
                                    targetSliceName: currentSession.targetSliceName,
                                    targetCustomerName: currentSession.targetCustomerName,
                                    expiresAt: currentSession.expiresAt,
                                    reason: currentSession.reason
                                }
                            });
                            console.log('[App] Valid impersonation session restored');
                        } else {
                            // Token invalid or expired
                            console.warn('[App] Impersonation token invalid or expired, clearing');
                            localStorage.removeItem('authToken');
                            store.dispatch({ type: 'clearImpersonation' });
                        }
                    } else if (authToken.startsWith('ws_')) {
                        // Workspace token - restore and let it validate on first API call
                        store.dispatch({
                            type: 'setImpersonation',
                            payload: {
                                token: authToken,
                                targetSliceId: null, // Will be set by /environment
                                targetSliceName: null,
                                expiresAt: null,
                                reason: null
                            }
                        });
                        console.log('[App] Workspace token restored');
                    } else {
                        // Regular session token - restore for axios interceptor
                        store.dispatch({
                            type: 'setImpersonation',
                            payload: {
                                token: authToken,
                                targetSliceId: null,
                                targetSliceName: null,
                                expiresAt: null,
                                reason: null
                            }
                        });
                        console.log('[App] Session token restored');
                    }
                } catch (error) {
                    // Only treat 401 as "token invalid". The previous broad catch
                    // wiped authToken on ANY error — including the 402 the
                    // subscription middleware used to throw on /impersonate/sessions
                    // when the impersonated workspace had no active subscription
                    // (now fixed in api/config/subscription-routes.ts, but the
                    // narrow catch hardens against future regressions and
                    // transient 5xx / network failures).
                    const status = error?.response?.status;
                    if (status === 401) {
                        console.warn('[App] Token validation 401, clearing:', error);
                        localStorage.removeItem('authToken');
                        const store = await loadStore();
                        store.dispatch({ type: 'clearImpersonation' });
                    } else {
                        console.warn('[App] Token validation failed (non-401, preserving token):', status, error?.message);
                    }
                }
            },

            async initUserContext() {
                try {
                    console.log('[App] Initializing user context from /auth/me');
                    const { tools } = await import('./services/tools');
                    // This will fetch user context and cache it
                    const userContext = await tools.getUserContext();
                    console.log('[App] User context loaded:', userContext.role);

                    // Dispatch to Redux so navbar subscription fires
                    const store = await loadStore();
                    store.dispatch({ type: 'setUserContext', payload: userContext });
                    
                    // Dispatch sliceId from /auth/me response if available
                    // This allows skipping workspace selection if user already has slice context
                    // IMPORTANT: Skip if impersonation token exists - let initImpersonation() set the correct sliceId
                    const authToken = localStorage.getItem('authToken');
                    const hasImpersonationToken = authToken && authToken.startsWith('imp_');

                    if (userContext.sliceId && !hasImpersonationToken) {
                        console.log('[App] SliceId from /auth/me:', userContext.sliceId);
                        store.dispatch({ type: 'setSliceId', payload: userContext.sliceId });
                    } else if (userContext.sliceId && hasImpersonationToken) {
                        console.log('[App] Skipping sliceId dispatch - impersonation token detected, will be set by initImpersonation()');
                    }

                    // Fetch customer data if user has customerId
                    if (userContext.customerId) {
                        await this.initCustomerData(userContext.customerId);
                    }

                    // Mark user as authenticated ONLY if we have a valid user ID
                    // Don't mark as authenticated just because we loaded a fallback user
                    const isValidUser = userContext && userContext.userUuid;
                    if (isValidUser) {
                        console.log('[App] Valid user authenticated:', userContext.userUuid);
                        this.update({ isAuthenticated: true });
                    } else {
                        console.log('[App] User context loaded but no valid user ID (likely unauthenticated):', userContext?.role);
                        this.update({ isAuthenticated: false });
                    }
                    
                    // Catch-all EULA gate for any auth path that landed the
                    // user inside the app with eulaAccepted=false (e.g. an
                    // OAuth callback that predates the SSOCallbackHandler
                    // EULA redirect, or a future provider that forgets it).
                    // Route through the login page's eulaRequired handler
                    // — opening the modal directly here is unsafe because
                    // initLocale runs *after* initUserContext, so the
                    // eula-acceptance-modal isn't in the DOM yet. The login
                    // page mounts the modal via its own component tree and
                    // handles returnTo on accept.
                    const currentRoute = getCurrentRoute();
                    const isOnLoginPage = currentRoute.includes('login');

                    if (!isOnLoginPage && userContext.eulaAccepted === false) {
                        console.log('[App] User has not accepted EULA, redirecting to login eularequired gate');
                        const returnTo = window.location.hash || '#';
                        const cleanReturn = returnTo.startsWith('#') ? returnTo.slice(1) : returnTo;
                        const safeReturn = cleanReturn || '/';
                        // Lowercase URL keys — see login.tag prop note.
                        window.location.href = `/#login?eularequired=true&returnto=${encodeURIComponent(safeReturn)}`;
                        return;
                    }
                } catch (error) {
                    console.error('[App] Failed to initialize user context:', error);

                    // Clear user context in Redux
                    const store = await loadStore();
                    store.dispatch({ type: 'setUserContext', payload: null });
                    
                    this.update({ isAuthenticated: false });
                    
                    // Preserve query parameters when redirecting to login
                    // This helper is defined in onMounted() - use preserveQueryParams() when available
                    // For now, preserve params manually in this catch block
                    const currentHash = window.location.hash;
                    if (currentHash && currentHash.includes('?')) {
                        const queryString = currentHash.split('?')[1];
                        console.log('[App] Preserving query params for login redirect (auth error):', queryString);
                        localStorage.setItem('cached_query_params', queryString);
                    }
                    
                    // Error is already handled in tools.getUserContext() with fallback to TENANT_USER
                }
            },
            
            async initFeatureFlags() {
                try {
                    console.log('[App] Initializing feature flags');
                    const { featureFlagService } = await import('./api/featureFlagService');
                    await featureFlagService.initialize();
                    console.log('[App] Feature flags loaded successfully');
                } catch (error) {
                    console.error('[App] Failed to initialize feature flags:', error);
                    // Don't fail the app if feature flags can't be loaded
                    // Features will fail open (allow by default)
                }
            },

            async initQuota() {
                try {
                    console.log('[App] Initializing quota details');
                    const { tools } = await import('./services/tools');
                    await tools.fetchAndUpdateQuota();
                    console.log('[App] Quota details loaded successfully');
                } catch (error) {
                    console.error('[App] Failed to initialize quota details:', error);
                    // Don't fail the app if quota can't be loaded
                    // Upload controls will assume quota is available
                }
            },

            async initSubscriptionStatus() {
                try {
                    console.log('[App] Initializing subscription status');
                    const { tools } = await import('./services/tools');
                    await tools.fetchAndUpdateSubscriptionStatus();
                    console.log('[App] Subscription status loaded successfully');
                } catch (error) {
                    console.error('[App] Failed to initialize subscription status:', error);
                    // Don't fail the app — banner and upload page fall back to local fetch.
                }
            },

            async initLocale() {
                try {
                    console.log('[App] Initializing locale service');
                    const { getLocaleService } = await import('./services/service-loader');
                    const localeService = await getLocaleService();
                    await localeService.initialize();

                    // Expose locale service globally for console debugging
                    window.locale = localeService;

                    console.log('[App] Locale service initialized successfully');
                    this.update({ localeLoaded: true });
                } catch (error) {
                    console.error('[App] Failed to initialize locale service:', error);
                    // Don't fail the app if locale can't be loaded
                    // Components will fall back to English
                    this.update({ localeLoaded: true }); // Still mark as loaded to unblock UI
                }
            },

            /**
             * Check if user's language preference differs from customer default
             * If user is admin and there's a mismatch, show reconciliation modal
             * If no mismatch, clear the user language preference (no override needed)
             */
            async checkLanguageReconciliation() {
                try {
                    console.log('[App] Checking language reconciliation');
                    const { getLocaleService } = await import('./services/service-loader');
                    const localeService = await getLocaleService();

                    const mismatch = localeService.hasLanguageMismatch();

                    if (mismatch.hasMismatch) {
                        console.log('[App] Language mismatch detected:', mismatch);

                        // Check if user is admin
                        const { tools } = await import('./services/tools');
                        const userContext = await tools.getUserContext();
                        const isAdmin = ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(userContext.role);

                        if (isAdmin) {
                            console.log('[App] User is admin - will show language reconciliation modal after mount');
                            // Show modal after component is fully mounted
                            // Use setTimeout to ensure modal component is available
                            setTimeout(() => {
                                if (window.languageReconciliationModal) {
                                    window.languageReconciliationModal.show(mismatch);
                                } else {
                                    console.warn('[App] Language reconciliation modal not available yet');
                                }
                            }, 500);
                        } else {
                            console.log('[App] User is not admin - no reconciliation modal needed');
                        }
                    } else {
                        console.log('[App] No language mismatch detected - clearing user language preference');
                        // Languages match, so no need to keep user preference override in localStorage
                        localeService.clearUserLanguagePreference();
                    }
                } catch (error) {
                    console.error('[App] Failed to check language reconciliation:', error);
                    // Non-critical - don't block app initialization
                }
            },

            async initCustomerData(customerId) {
                try {
                    console.log('[App] Fetching customer data for customerId:', customerId);
                    const { customerService } = await import('./api/customerService.js');
                    const customerData = await customerService.getById(customerId, '', { skipLoading: true });
                    console.log('[App] Customer data loaded:', customerData.name);

                    // Dispatch full customer object to Redux
                    const store = await loadStore();
                    store.dispatch({
                        type: 'SET_CUSTOMER',
                        payload: {
                            customerId: customerData.customerId,
                            name: customerData.displayName,
                            logoUrl: customerData.logoUrl,
                            defaultLanguage: customerData.defaultLanguage
                        }
                    });
                } catch (error) {
                    console.error('[App] Failed to fetch customer data:', error);
                    // Don't throw - customer data is not critical for app to function
                    const store = await loadStore();
                    store.dispatch({ type: 'CLEAR_CUSTOMER' });
                }
            },

            async checkAndRedirectToWorkspaceSelection() {
                try {
                    // Skip workspace checks for public pages
                    if (this.isOnPublicPage()) {
                        console.log('[App] On public page, skipping workspace checks');
                        return;
                    }
                    
                    // Lazy import tools (needed for workspace selection logic)
                    let tools;
                    try {
                        const imported = await import('./services/tools');
                        tools = imported.tools;
                        console.log('[App] [DEBUG] tools imported successfully');
                    } catch (importError) {
                        console.error('[App] [ERROR] Failed to import tools:', importError);
                        return;
                    }
                    
                    // IMPERSONATION DEBUG: Check for impersonation token
                    const authToken = localStorage.getItem('authToken');
                    const isImpersonating = authToken && authToken.startsWith('imp_');
                    if (isImpersonating) {
                        console.log('[App] [IMPERSONATION DEBUG] checkAndRedirectToWorkspaceSelection called with impersonation token');
                    }
                    
                    // Get current slice context DIRECTLY from Redux store (populated by /environment call)
                    // Don't use this.state.sliceId because it might not be updated yet
                    const store = await loadStore();
                    const sliceId = store.getState().sliceId;
                    
                    // Get user context
                    let userContext;
                    try {
                        userContext = await tools.getUserContext();
                        console.log('[App] [DEBUG] User context retrieved:', userContext?.role);
                    } catch (contextError) {
                        console.error('[App] [ERROR] getUserContext failed:', contextError);
                        throw contextError;
                    }
                    
                    const isPrivilegedUser = ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(userContext.role);
                    console.log('[App] [DEBUG] isPrivilegedUser calculated:', isPrivilegedUser);
                    
                    // Check if user is on the workspace selection page
                    const currentRoute = getCurrentRoute();
                    console.log('[App] [DEBUG] currentRoute:', currentRoute);
                    
                    const isOnWorkspaceSelection = currentRoute.includes('select-workspace');
                    const isOnHome = currentRoute.includes('home') || currentRoute === '#' || currentRoute === '';
                    
                    console.log('[App] Workspace check:', {
                        sliceId,
                        role: userContext.role,
                        isPrivilegedUser,
                        currentRoute,
                        isOnWorkspaceSelection,
                        isOnHome,
                        currentComponent: this.state.currentComponent,
                        hasAuthToken: !!authToken,
                        isImpersonating: isImpersonating
                    });
                    
                    // Check if user is on a page that doesn't need slice (admin or user pages)
                    const isOnAdmin = this.isOnAdminPage();
                    const isOnUserPage = this.isOnUserPage();
                    
                    // If environment hasn't fully loaded yet, wait for it
                    // This prevents premature redirects before sliceId is available
                    if (!this.state.environmentLoaded) {
                        console.log('[App] Environment not loaded yet, waiting...');
                        return;
                    }

                    // METALARK-929: a non-entered PARTNER_ADMIN has no workspace of
                    // its own — its role row is partner-scoped (customerId='partner',
                    // no sliceId). Land it on the partner "My Customers" page (where
                    // it enters a customer) instead of the empty workspace selector.
                    // Once it enters a customer it runs as effective CUSTOMER_ADMIN
                    // (role no longer PARTNER_ADMIN), so this branch stops firing.
                    if (userContext.role === 'PARTNER_ADMIN') {
                        if (!isOnAdmin && !currentRoute.includes('partner-dashboard') && !currentRoute.includes('partner-customers')) {
                            console.log('[App] PARTNER_ADMIN with no workspace — routing to partner dashboard landing');
                            navigateTo('partner-dashboard');
                        }
                        return; // partner never needs a workspace; skip workspace-selection logic
                    }

                    // If we have a workspace token but no sliceId yet AND environment hasn't loaded,
                    // trigger environment reload (can happen when navigating back after workspace cleared)
                    // This applies to all workspace-dependent pages (home, reports, content, upload, etc.)
                    const requiresWorkspace = !isOnWorkspaceSelection && !isOnAdmin && !isOnUserPage;
                    if (!sliceId && requiresWorkspace && localStorage.getItem('authToken') && !this.state.environmentLoaded) {
                        console.log('[App] On workspace-dependent page with token but no environment, reloading...');
                        // Trigger environment reload
                        this.initSession();
                        return;
                    }
                    
                    // If no slice context and not on selection, admin, or user page, check for auto-selection
                    if (!sliceId && requiresWorkspace) {
                        console.log('[App] No slice context, checking for auto-selection opportunity');
                        
                        // IMPORTANT: Only clear tokens if environment has loaded
                        // Don't clear fresh tokens before they've been validated
                        // Don't clear impersonation tokens!
                        const hasToken = localStorage.getItem('authToken');
                        if (hasToken && this.state.environmentLoaded && !hasToken.startsWith('imp_')) {
                            console.log('[App] Clearing stale authToken before workspace selection (environment loaded, still no sliceId)');
                            localStorage.removeItem('authToken');
                            store.dispatch({ type: 'clearImpersonation' });
                        } else if (hasToken && hasToken.startsWith('imp_')) {
                            console.log('[App] [IMPERSONATION DEBUG] NOT clearing impersonation token, waiting for environment to load');
                        } else if (hasToken && !this.state.environmentLoaded) {
                            console.log('[App] Have token but environment not loaded, waiting before redirect');
                            return; // Don't redirect yet, let environment load first
                        }
                        
                        // Check if we have an impersonation token before attempting auto-selection
                        const authToken = localStorage.getItem('authToken');
                        if (authToken && authToken.startsWith('imp_')) {
                            console.log('[App] [IMPERSONATION DEBUG] Has impersonation token, staying on home to let environment load');
                            return; // Stay on current page, let /environment endpoint load the sliceId
                        }
                        
                        // Try to auto-select if only one workspace available
                        const autoSelected = await this.tryAutoSelectWorkspace();
                        if (autoSelected) {
                            console.log('[App] Auto-selected workspace, staying on home with skeleton');
                            return; // Stay on home page, skeleton will remain visible until sliceId is set
                        }
                        
                        console.log('[App] Auto-selection not possible, redirecting to workspace selection');
                        navigateTo('select-workspace');
                        return;
                    }
                    
                    // Auto-redirect off the workspace picker only when the
                    // caller has exactly one direct-role workspace — that's
                    // the single-native-workspace case (METALARK-738) where
                    // the picker is a no-op. Multi-workspace admins must
                    // stay on the picker so they can choose; otherwise a
                    // stale Redux sliceId (mock /auth/me re-emitting the
                    // last selection, or a JWT slot left behind by an
                    // older session) silently routes them away before they
                    // can pick.
                    if (sliceId && isOnWorkspaceSelection) {
                        const directRoleCount = countDirectRoleWorkspaces(userContext);
                        if (directRoleCount === 1) {
                            console.log('[App] Single direct-role workspace — redirecting from workspace selection to home');
                            navigateTo('home');
                        } else {
                            console.log('[App] Multiple direct-role workspaces — staying on picker so the admin can choose (count:', directRoleCount + ')');
                        }
                    }
                    
                    // If on workspace selection page, ensure no bearer tokens are active
                    // EXCEPTION: Don't clear impersonation tokens - those should be preserved
                    if (isOnWorkspaceSelection) {
                        const hasToken = store.getState().impersonation?.token;
                        const authToken = localStorage.getItem('authToken');
                        const isImpersonating = authToken && authToken.startsWith('imp_');
                        
                        if (hasToken && !isImpersonating) {
                            console.log('[App] On workspace selection with non-impersonation token in Redux, clearing it');
                            localStorage.removeItem('authToken');
                            const store = await loadStore();
                            store.dispatch({ type: 'clearImpersonation' });
                        } else if (isImpersonating) {
                            console.log('[App] [IMPERSONATION DEBUG] On workspace selection with impersonation token, preserving it');
                        }
                    }
                } catch (error) {
                    console.error('[App] Failed to check workspace selection redirect:', error);
                }
            },

            async tryAutoSelectWorkspace() {
                try {
                    // Check if we already tried auto-selection (prevents loops)
                    const alreadyTried = sessionStorage.getItem('workspace_auto_select_attempted');
                    if (alreadyTried) {
                        console.log('[App] Already attempted auto-selection, skipping');
                        return false;
                    }
                    
                    // Check if we have an active impersonation token
                    // If so, skip auto-selection and let the impersonation flow complete
                    const authToken = localStorage.getItem('authToken');
                    if (authToken && authToken.startsWith('imp_')) {
                        console.log('[App] [IMPERSONATION DEBUG] Impersonation token detected, skipping auto-selection to preserve session');
                        console.log('[App] [IMPERSONATION DEBUG] Token:', authToken.substring(0, 20) + '...');
                        return false;
                    }
                    
                    // Mark that we're attempting auto-selection
                    sessionStorage.setItem('workspace_auto_select_attempted', 'true');
                    
                    console.log('[App] Checking available workspaces for auto-selection');
                    const { workspaceService } = await import('./api/workspaceService.js');
                    const response = await workspaceService.getAvailableWorkspaces();
                    const workspaces = response.workspaces || [];
                    
                    console.log('[App] Available workspaces:', workspaces.length);
                    
                    // Filter to workspaces where user has an actual role (not just admin access)
                    const workspacesWithRole = workspaces.filter(w => w.roleInSlice);
                    console.log('[App] Workspaces with role:', workspacesWithRole.length);
                    
                    // If user has exactly one workspace with a role, auto-select it
                    if (workspacesWithRole.length === 1) {
                        const workspace = workspacesWithRole[0];
                        console.log('[App] Found single workspace with role, auto-selecting:', workspace.sliceId);
                        
                        // Call select-workspace API
                        const result = await workspaceService.selectWorkspace(workspace.sliceId);
                        const token = result.token || result.data?.token;
                        const sliceId = result.sliceId || workspace.sliceId;
                        
                        if (!token) {
                            console.error('[App] No token received from auto-selection');
                            return false;
                        }
                        
                        // Store token and workspace info
                        localStorage.setItem('authToken', token);
                        localStorage.setItem('workspace_sliceId', sliceId);
                        localStorage.setItem('workspace_sliceName', workspace.sliceName);
                        localStorage.setItem('last_selected_slice_id', sliceId);
                        
                        // Update Redux with token
                        const store = await loadStore();
                        store.dispatch({
                            type: 'setImpersonation',
                            payload: { token }
                        });
                        
                        // Update Redux with sliceId
                        store.dispatch({ type: 'setSliceId', payload: sliceId });
                        
                        console.log('[App] Single role workspace auto-selected, sliceId:', sliceId);
                        
                        // Clear the attempt flag after successful selection
                        sessionStorage.removeItem('workspace_auto_select_attempted');
                        
                        return true;
                    }
                    
                    // Multi-direct-role users always see the picker — no
                    // silent auto-restore from last_selected_slice_id.
                    // The previous "if you picked slice-A last time, drop
                    // you back into slice-A" behavior hides the picker
                    // from users who deliberately wanted to switch and
                    // contradicts the forced-pick path that routes.js
                    // already does for admin-tier users. last_selected_
                    // slice_id is still written so future surfaces (e.g.
                    // a "Recently used" hint on the picker) can read it.

                    // If exactly one workspace, auto-select it
                    if (workspaces.length === 1) {
                        const workspace = workspaces[0];
                        console.log('[App] Found single workspace, auto-selecting:', workspace.sliceId);
                        
                        // Call select-workspace API
                        const result = await workspaceService.selectWorkspace(workspace.sliceId);
                        const token = result.token || result.data?.token;
                        const sliceId = result.sliceId || workspace.sliceId;
                        
                        if (!token) {
                            console.error('[App] No token received from auto-selection');
                            return false;
                        }
                        
                        // Store token and workspace info
                        localStorage.setItem('authToken', token);
                        localStorage.setItem('workspace_sliceId', sliceId);
                        localStorage.setItem('workspace_sliceName', workspace.sliceName);
                        localStorage.setItem('last_selected_slice_id', sliceId);
                        
                        // Update Redux with token
                        const store = await loadStore();
                        store.dispatch({
                            type: 'setImpersonation',
                            payload: { token }
                        });
                        
                        // Update Redux with sliceId
                        store.dispatch({ type: 'setSliceId', payload: sliceId });
                        
                        console.log('[App] Auto-selection complete, sliceId:', sliceId);
                        
                        // Clear the attempt flag after successful selection
                        sessionStorage.removeItem('workspace_auto_select_attempted');
                        
                        // Only navigate to home if we're on the workspace selection page
                        // Otherwise stay on the current page and let it load with the new workspace context
                        const currentRoute = getCurrentRoute();
                        if (currentRoute.includes('select-workspace') || !currentRoute || currentRoute === '#') {
                            setTimeout(() => {
                                console.log('[App] Auto-selected workspace, navigating to home');
                                navigateTo('home');
                            }, 100);
                        } else {
                            console.log('[App] Auto-selected workspace, staying on current page:', currentRoute);
                        }
                        
                        return true;
                    }
                    
                    console.log('[App] Multiple or no workspaces, cannot auto-select');
                    return false;
                } catch (error) {
                    console.error('[App] Auto-selection failed:', error);
                    // Clear the attempt flag so user can try again
                    sessionStorage.removeItem('workspace_auto_select_attempted');
                    return false;
                }
            }

        }
    </script>

    <style>
        .app {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
        }


        .app .page, [is="app"] .page {
            padding: 2rem;
            padding-bottom: 0;
            /*max-width: 800px;*/
            margin: 0 auto;
        }

        #route-content > * {
            display: flex;
            flex: 1;
            flex-direction: column;
        }

        .workspace-loading {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .public-page-loading {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 2rem;
        }

    </style>
</app>